import { ReactNode, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/hooks/use-language";
import { useAuth } from "@/hooks/use-auth";
import { useEvents } from "@/hooks/use-events";
import { EventData } from "@/contexts/event/EventContext";
import { format } from "date-fns";
import { nb, enUS } from "date-fns/locale";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Clock, MapPin, Users, TrendingUp, UserCheck, UserX } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { LoadingButton } from "@/components/ui/loading-button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";

interface EventTooltipProps {
  event: EventData;
  children: ReactNode;
  isMobile?: boolean;
}

export function EventTooltip({ event, children, isMobile = false }: EventTooltipProps) {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const { signUp, cancel, joinWaitingList, leaveWaitingList } = useEvents();
  const { toast } = useToast();
  const locale = language === 'no' ? nb : enUS;

  const participantCount = event.participants?.length || 0;
  const waitlistCount = event.waitingList?.length || 0;
  const availableSpots = event.maxParticipants - participantCount;
  
  // Determine user's participation status
  const isParticipant = user ? event.participants?.includes(user.id) : false;
  const isOnWaitingList = user ? event.waitingList?.includes(user.id) : false;
  const eventIsFull = participantCount >= event.maxParticipants;

  // Handle signup/cancel/waitlist actions
  const handleAction = async () => {
    if (!user) {
      toast({
        title: t("auth.required"),
        variant: "destructive",
      });
      return;
    }
    
    setIsLoading(true);
    try {
      if (isParticipant) {
        await cancel(event.id, user.id);
      } else if (isOnWaitingList) {
        await leaveWaitingList(event.id, user.id);
      } else if (eventIsFull) {
        await joinWaitingList(event.id, user.id);
      } else {
        await signUp(event.id, user.id);
      }
      // Close the sheet after successful action on mobile
      if (isMobile) {
        setOpen(false);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Determine button appearance
  const getButtonConfig = () => {
    if (isParticipant) {
      return {
        variant: "destructive" as const,
        text: t("calendar.leave"),
        icon: <UserX className="mr-2 h-4 w-4" />,
      };
    }
    if (isOnWaitingList) {
      return {
        variant: "outline" as const,
        text: t("calendar.leave_waitlist"),
        icon: <UserX className="mr-2 h-4 w-4" />,
      };
    }
    if (eventIsFull) {
      return {
        variant: "secondary" as const,
        text: t("calendar.join_waitlist"),
        icon: <UserCheck className="mr-2 h-4 w-4" />,
      };
    }
    return {
      variant: "default" as const,
      text: t("calendar.signup"),
      icon: <UserCheck className="mr-2 h-4 w-4" />,
    };
  };

  const buttonConfig = getButtonConfig();

  // Shared event details content
  const eventDetailsContent = (
    <div className="space-y-3 pb-20">
      {/* Title */}
      <div>
        <h4 className="font-semibold text-base">
          {event.titleKey ? t(event.titleKey) : event.title}
        </h4>
        <p className="text-sm text-muted-foreground">
          {event.descriptionKey ? t(event.descriptionKey) : event.description}
        </p>
      </div>

      {/* Event Details */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm">
          <Clock className="h-4 w-4 text-muted-foreground" />
          <span>
            {format(new Date(event.date), 'EEEE, d MMMM', { locale })} • {event.time}
          </span>
        </div>

        <div className="flex items-center gap-2 text-sm">
          <MapPin className="h-4 w-4 text-muted-foreground" />
          <span>{event.location}</span>
        </div>

        <div className="flex items-center gap-2 text-sm">
          <TrendingUp className="h-4 w-4 text-muted-foreground" />
          <span>{event.groupKey ? t(event.groupKey) : event.group}</span>
        </div>
      </div>

      {/* Capacity */}
      <div className="pt-2 border-t">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">
              {t("calendar.capacity")}: {participantCount}/{event.maxParticipants}
            </span>
          </div>
          {availableSpots > 0 ? (
            <Badge variant="secondary" className="text-xs">
              {availableSpots} {t("calendar.available_spots")}
            </Badge>
          ) : (
            <Badge variant="destructive" className="text-xs">
              {t("calendar.full")}
            </Badge>
          )}
        </div>

        {/* Participants List */}
        {event.participantDetails && event.participantDetails.length > 0 && (
          <div className="mt-2">
            <p className="text-xs font-medium text-muted-foreground mb-1">
              {t("calendar.signed_up")}:
            </p>
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {event.participantDetails.map((participant) => (
                <div key={participant.id} className="text-xs flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                  {participant.name}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Waitlist */}
        {waitlistCount > 0 && (
          <div className="mt-2 pt-2 border-t">
            <p className="text-xs text-muted-foreground">
              {waitlistCount} {t("event.waitlist.count")}
            </p>
          </div>
        )}
      </div>
    </div>
  );

  // Mobile: Use Sheet for click interaction
  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          {children}
        </SheetTrigger>
        <SheetContent side="bottom" className="max-h-[70vh] pb-4">
          <SheetHeader>
            <SheetTitle>
              {event.titleKey ? t(event.titleKey) : event.title}
            </SheetTitle>
          </SheetHeader>
          <ScrollArea className="h-[calc(70vh-8rem)] mt-4">
            {eventDetailsContent}
            <div className="mt-4 pt-4 border-t flex gap-2">
              {user && (
                <LoadingButton
                  variant={buttonConfig.variant}
                  onClick={handleAction}
                  loading={isLoading}
                  className="flex-1"
                >
                  {buttonConfig.icon}
                  {buttonConfig.text}
                </LoadingButton>
              )}
              <Button 
                variant="outline"
                onClick={() => {
                  setOpen(false);
                  navigate(`/event/${event.id}`);
                }} 
                className={user ? "flex-1" : "w-full"}
              >
                {t("calendar.view_details")} →
              </Button>
            </div>
          </ScrollArea>
        </SheetContent>
      </Sheet>
    );
  }

  // Desktop: Use Tooltip for hover interaction, but make clickable
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div onClick={() => navigate(`/event/${event.id}`)} className="cursor-pointer">
            {children}
          </div>
        </TooltipTrigger>
        <TooltipContent 
          side="right" 
          className="w-80 p-4 bg-popover border-border"
          sideOffset={5}
        >
          {eventDetailsContent}
          <div className="mt-3 pt-3 border-t">
            <p className="text-xs text-muted-foreground text-center">
              {t("calendar.click_to_view")} →
            </p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
